import { createContext, useContext, type ReactNode } from 'react';
import type {
  ModuleKey,
  Permission,
  PlatformPermission,
  SessionContext,
} from '@dashboard/contracts';

/**
 * The authenticated session as the UI sees it.
 *
 * `can()` decides whether to render a control. It is a usability affordance
 * only -- the server re-checks every request, and section 13.2 is explicit that
 * hiding a button is not an authorization mechanism.
 */

interface SessionValue {
  session: SessionContext;
  can: (...permissions: Permission[]) => boolean;
  canAny: (...permissions: Permission[]) => boolean;
  /**
   * The provider plane (contracts: platform.ts). Separate from `can` on
   * purpose: the two permission sets never overlap, and one function that
   * searched both would let a typo in a tenant permission be quietly satisfied
   * by a platform grant.
   */
  canPlatform: (...permissions: PlatformPermission[]) => boolean;
  /** True for a service-provider account: it holds anything on that plane. */
  isProvider: boolean;
  /**
   * True for a member of any organization but the host -- a supplier's people
   * (docs/organizations-design.md). The server has already narrowed
   * `permissions` and scopes every row, so this is for saying whose data is on
   * screen and for leaving out choices that could only be refused.
   */
  isExternal: boolean;
  /**
   * Whether this deployment has a module at all.
   *
   * Rarely needed. Module selection already reaches the UI through
   * `session.permissions`, which the server filtered -- so `canAny('cost.read')`
   * is false in a deployment without Costing and every existing check kept
   * working unchanged. This is for the handful of places that must distinguish
   * "you may not" from "there is no such thing here".
   */
  hasModule: (module: ModuleKey) => boolean;
}

const Context = createContext<SessionValue | null>(null);

export function SessionProvider({
  session,
  children,
}: {
  session: SessionContext;
  children: ReactNode;
}) {
  const held = new Set(session.permissions);
  const heldPlatform = new Set<string>(session.platformPermissions);
  const modules = new Set<string>(session.deployment.modules);

  const value: SessionValue = {
    session,
    can: (...permissions) => permissions.every((permission) => held.has(permission)),
    canAny: (...permissions) => permissions.some((permission) => held.has(permission)),
    canPlatform: (...permissions) =>
      permissions.every((permission) => heldPlatform.has(permission)),
    isProvider: session.platformPermissions.length > 0,
    isExternal: !session.memberOf.isHost,
    hasModule: (module) => modules.has(module),
  };

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useSession(): SessionValue {
  const context = useContext(Context);
  if (!context) throw new Error('useSession must be used inside SessionProvider');
  return context;
}
